/**
 * Owner: Masa Bayu
 * File: face-text-paint.ts
 * Date:Time: 2026-10-09 18:36
 * Description: QX-URP-04 Face paint · direct resolve-* imports · no tip mutate.
 */

import type { CSSProperties } from 'react';
import {
  resolveOpacity,
  resolveRotation,
  resolveVisibility,
} from './resolve-absence';
import { resolveWrapPolicy } from './resolve-wrap';
import type {
  PhysicalLineEvidence,
  PlacementModeEvidence,
  WrapPolicy,
  WitnessedBoolean,
  WitnessedNumber,
} from './types';

export type FaceTextPaintEvidence = {
  readonly preserveSource: boolean;
  readonly placementMode: PlacementModeEvidence;
  /**
   * Explicit physical-line witness. Face preserve-source absolute cells
   * use PROVEN_SINGLE_LINE (COVER / Accept-Law line atoms). Do not invent
   * PROVEN from visual appearance alone.
   */
  readonly physicalLine: PhysicalLineEvidence;
  readonly rotation?: WitnessedNumber;
  readonly opacity?: WitnessedNumber;
  readonly visibility?: WitnessedBoolean;
};

export type FaceTextWrapPaint = {
  readonly wrapPolicy: WrapPolicy;
  readonly whiteSpace: CSSProperties['whiteSpace'] | undefined;
  readonly overflow: CSSProperties['overflow'] | undefined;
  readonly overflowWrap: CSSProperties['overflowWrap'] | undefined;
  readonly wordBreak: CSSProperties['wordBreak'] | undefined;
  /** DOM mark · null when flow / no URP wrap attr. */
  readonly dataWrapPolicy: string | null;
};

export type FaceTextAbsencePaint = {
  readonly rotation: number;
  readonly rotationProvenance: 'source' | 'absence-default';
  readonly opacity: number;
  readonly opacityProvenance: 'source' | 'absence-default';
  readonly visibility: boolean;
  readonly visibilityProvenance: 'source' | 'absence-default';
};

/**
 * Face convention: preserve-source ∧ absolute placement ⇒ physical-line cell
 * (same evidence class as COVER-TEXT-WRAP-01 / SPSU line atoms).
 * Otherwise UNKNOWN — never invent PROVEN_SINGLE_LINE.
 */
export function facePhysicalLineEvidence(
  preserveSource: boolean,
  placementMode: PlacementModeEvidence,
): PhysicalLineEvidence {
  if (preserveSource && placementMode === 'ABSOLUTE') {
    return 'PROVEN_SINGLE_LINE';
  }
  return 'UNKNOWN';
}

/** Map URP wrapPolicy → paint styles. UNKNOWN/FLOW never force nowrap. */
export function faceTextWrapPaint(
  evidence: Pick<
    FaceTextPaintEvidence,
    'preserveSource' | 'placementMode' | 'physicalLine'
  >,
): FaceTextWrapPaint {
  const wrapPolicy = resolveWrapPolicy({
    preserveSource: evidence.preserveSource,
    placementMode: evidence.placementMode,
    physicalLine: evidence.physicalLine,
  });
  if (wrapPolicy === 'PHYSICAL_LINE_NO_WRAP') {
    return {
      wrapPolicy,
      whiteSpace: 'nowrap',
      overflow: 'visible',
      overflowWrap: 'normal',
      wordBreak: 'normal',
      dataWrapPolicy: 'physical-line-nowrap',
    };
  }
  if (wrapPolicy === 'FLOW') {
    return {
      wrapPolicy,
      whiteSpace: undefined,
      overflow: undefined,
      overflowWrap: undefined,
      wordBreak: undefined,
      dataWrapPolicy: null,
    };
  }
  return {
    wrapPolicy,
    whiteSpace: undefined,
    overflow: undefined,
    overflowWrap: undefined,
    wordBreak: undefined,
    dataWrapPolicy: 'unknown',
  };
}

/** STANDARD absence defaults for Face · source wins when present. */
export function faceTextAbsencePaint(
  evidence: Pick<FaceTextPaintEvidence, 'rotation' | 'opacity' | 'visibility'>,
): FaceTextAbsencePaint {
  const rotation = resolveRotation(evidence.rotation);
  const opacity = resolveOpacity(evidence.opacity);
  const visibility = resolveVisibility(evidence.visibility);
  return {
    rotation: rotation.value,
    rotationProvenance: rotation.provenance,
    opacity: opacity.value,
    opacityProvenance: opacity.provenance,
    visibility: visibility.value,
    visibilityProvenance: visibility.provenance,
  };
}
